import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { adminManagementService } from '../../services/adminService';
import {
    ChevronLeft,
    Save,
    Plus,
    CreditCard,
    Users,
    Layers,
    Activity,
    AlertCircle,
    CheckCircle,
    Mail,
    Clock
} from 'lucide-react';
import './AdminPages.css';
import './AdminWorkspaceDetail.css';

function AdminWorkspaceDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [workspace, setWorkspace] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [subForm, setSubForm] = useState({
        plan: 'free',
        status: 'active',
        quotas: {},
        usageStats: {}
    });

    useEffect(() => {
        loadWorkspace();
    }, [id]);

    const loadWorkspace = async () => {
        try {
            setLoading(true);
            const data = await adminManagementService.getWorkspaceDetail(id);
            setWorkspace(data);
            if (data.subscription) {
                setSubForm({
                    plan: data.subscription.plan,
                    status: data.subscription.status,
                    quotas: data.subscription.quotas || {},
                    usageStats: data.subscription.usageStats || {}
                });
            } else {
                // Default values for new subscription
                setSubForm({
                    plan: 'free',
                    status: 'active',
                    quotas: {
                        executionMinutesPerMonth: 20,
                        maxTeamMembers: 1,
                        maxApps: 1,
                        maxDeviceConfigs: 2,
                        maxTestSuites: 5,
                        maxConcurrency: 1,
                        realDevicesEnabled: false
                    },
                    usageStats: {
                        executionMinutesThisMonth: 0,
                        testExecutionsThisMonth: 0
                    }
                });
            }
        } catch (err) {
            setError('Failed to load workspace details');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleSubChange = (e) => {
        const { name, value } = e.target;
        setSubForm(prev => ({ ...prev, [name]: value }));
    };

    const handleQuotaChange = (key, value, type = 'number') => {
        setSubForm(prev => ({
            ...prev,
            quotas: { ...prev.quotas, [key]: type === 'boolean' ? value : (parseInt(value) || 0) }
        }));
    };

    const handleUsageChange = (key, value) => {
        setSubForm(prev => ({
            ...prev,
            usageStats: { ...prev.usageStats, [key]: parseInt(value) || 0 }
        }));
    };

    const handleSubmitSubscription = async (e) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);
        try {
            if (workspace.subscription) {
                await adminManagementService.updateSubscription(workspace.subscription.id, subForm);
                setSuccess('Subscription updated successfully');
            } else {
                const newSub = await adminManagementService.createSubscription(id, subForm);
                setWorkspace(prev => ({ ...prev, subscription: newSub }));
                setSuccess('Subscription created successfully');
            }
            setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to save subscription');
        }
    };

    if (loading) return <div className="admin-loading">Loading workspace details...</div>;
    if (!workspace) return <div className="admin-error">Workspace not found</div>;

    const quotas = subForm.quotas;
    const usage = subForm.usageStats;

    return (
        <div className="admin-page">
            <div className="admin-breadcrumb">
                <Link to="/admin/workspaces">
                    <ChevronLeft size={16} />
                    Back to Workspaces
                </Link>
            </div>

            <div className="workspace-detail-header">
                <div>
                    <h1>{workspace.name}</h1>
                    <p className="workspace-id">ID: {workspace.id} • Slug: {workspace.slug}</p>
                </div>
                {workspace.isActive ? (
                    <span className="status-badge success">Active</span>
                ) : (
                    <span className="status-badge danger">Inactive</span>
                )}
            </div>

            <div className="detail-grid">
                {/* Info Card */}
                <div className="detail-card">
                    <div className="card-header">
                        <Users size={20} />
                        <h3>Owner Information</h3>
                    </div>
                    <div className="card-body">
                        <div className="info-row">
                            <span className="info-label">Full Name:</span>
                            <span className="info-value">{workspace.owner?.fullName || 'N/A'}</span>
                        </div>
                        <div className="info-row">
                            <span className="info-label">Email:</span>
                            <span className="info-value">{workspace.owner?.email}</span>
                        </div>
                        <div className="info-row">
                            <span className="info-label">Owner ID:</span>
                            <span className="info-value">{workspace.ownerId}</span>
                        </div>
                    </div>
                </div>


            </div>
            {/* Subscription Card */}
            <div className="detail-card">
                <div className="card-header">
                    <CreditCard size={20} />
                    <h3>Subscription & Limits</h3>
                </div>
                <form className="card-body sub-form" onSubmit={handleSubmitSubscription}>
                    {error && <div className="admin-form-error"><AlertCircle size={18} /> {error}</div>}
                    {success && <div className="admin-form-success"><CheckCircle size={18} /> {success}</div>}

                    <div className="form-section">
                        <h4>Base Settings</h4>
                        <div className="form-row">
                            <div className="form-group">
                                <label>Plan</label>
                                <select name="plan" value={subForm.plan} onChange={handleSubChange}>
                                    <option value="free">Free</option>
                                    <option value="basic">Basic</option>
                                    <option value="pro">Pro</option>
                                    <option value="business">Business</option>
                                    <option value="enterprise">Enterprise</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Status</label>
                                <select name="status" value={subForm.status} onChange={handleSubChange}>
                                    <option value="active">Active</option>
                                    <option value="canceled">Canceled</option>
                                    <option value="past_due">Past Due</option>
                                    <option value="paused">Paused</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    <div className="form-section">
                        <h4>Quotas (JSON)</h4>
                        <div className="quota-grid">
                            <div className="form-group">
                                <label>Monthly Minutes (-1 = Inf)</label>
                                <input
                                    type="number"
                                    value={quotas.executionMinutesPerMonth ?? 0}
                                    onChange={(e) => handleQuotaChange('executionMinutesPerMonth', e.target.value)}
                                />
                            </div>
                            <div className="form-group">
                                <label>Max Team Members</label>
                                <input
                                    type="number"
                                    value={quotas.maxTeamMembers ?? 0}
                                    onChange={(e) => handleQuotaChange('maxTeamMembers', e.target.value)}
                                />
                            </div>
                            <div className="form-group">
                                <label>Max Apps</label>
                                <input
                                    type="number"
                                    value={quotas.maxApps ?? 0}
                                    onChange={(e) => handleQuotaChange('maxApps', e.target.value)}
                                />
                            </div>
                            <div className="form-group">
                                <label>Max Test Suites</label>
                                <input
                                    type="number"
                                    value={quotas.maxTestSuites ?? 0}
                                    onChange={(e) => handleQuotaChange('maxTestSuites', e.target.value)}
                                />
                            </div>
                            <div className="form-group">
                                <label>Max Concurrency</label>
                                <input
                                    type="number"
                                    value={quotas.maxConcurrency ?? 1}
                                    onChange={(e) => handleQuotaChange('maxConcurrency', e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="quota-grid" style={{ marginTop: '0.5rem' }}>
                            <div className="" style={{ flexDirection: 'row', alignItems: 'center', gap: '10px' }}>
                                <input
                                    type="checkbox"
                                    id="realDevicesEnabled"
                                    checked={quotas.realDevicesEnabled || false}
                                    onChange={(e) => handleQuotaChange('realDevicesEnabled', e.target.checked, 'boolean')}
                                />
                                <label htmlFor="realDevicesEnabled" style={{ marginBottom: 0 }}>Enable Real Devices</label>
                            </div>
                        </div>
                    </div>

                    <div className="form-section">
                        <h4>Usage Stats (JSON)</h4>
                        <div className="quota-grid">
                            <div className="form-group">
                                <label>Execution Minutes Used</label>
                                <input
                                    type="number"
                                    value={usage.executionMinutesThisMonth ?? 0}
                                    onChange={(e) => handleUsageChange('executionMinutesThisMonth', e.target.value)}
                                />
                            </div>
                            <div className="form-group">
                                <label>Test Executions Count</label>
                                <input
                                    type="number"
                                    value={usage.testExecutionsThisMonth ?? 0}
                                    onChange={(e) => handleUsageChange('testExecutionsThisMonth', e.target.value)}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="form-actions">
                        <button type="submit" className="admin-save-btn">
                            <Save size={18} />
                            {workspace.subscription ? 'Update Subscription' : 'Create Subscription'}
                        </button>
                    </div>
                </form>
            </div>
            {/* Members Section */}
            <div className="detail-section">
                <div className="section-header">
                    <Users size={20} />
                    <h3>Workspace Members ({workspace.members?.length || 0})</h3>
                </div>
                <div className="admin-table-container">
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th>User</th>
                                <th>Email</th>
                                <th>Role</th>
                                <th>Joined</th>
                            </tr>
                        </thead>
                        <tbody>
                            {workspace.members?.map(member => (
                                <tr key={member.id}>
                                    <td>
                                        <div className="user-cell">
                                            <div className="user-avatar-sm">
                                                {member.user?.fullName?.[0] || member.user?.email?.[0] || 'U'}
                                            </div>
                                            <span>{member.user?.fullName || 'No Name'}</span>
                                        </div>
                                    </td>
                                    <td>{member.user?.email}</td>
                                    <td>
                                        <span className={`role-badge ${member.role}`}>
                                            {member.role}
                                        </span>
                                    </td>
                                    <td>{new Date(member.createdAt).toLocaleDateString()}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Apps Section */}
            <div className="detail-section">
                <div className="section-header">
                    <Layers size={20} />
                    <h3>Uploaded Apps ({workspace.apps?.length || 0})</h3>
                </div>
                <div className="admin-table-container">
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Platform</th>
                                <th>Version</th>
                                <th>Uploader</th>
                                <th>Uploaded At</th>
                            </tr>
                        </thead>
                        <tbody>
                            {workspace.apps?.length > 0 ? (
                                workspace.apps.map(app => (
                                    <tr key={app.id}>
                                        <td>
                                            <Link to={`/admin/apps/${app.id}`} className="admin-link">
                                                {app.name || 'Untitled App'}
                                            </Link>
                                        </td>
                                        <td>
                                            <span className={`platform-badge ${app.platform}`}>
                                                {app.platform}
                                            </span>
                                        </td>
                                        <td>{app.version || '1.0.0'}</td>
                                        <td>{app.uploader?.email || 'System'}</td>
                                        <td>{new Date(app.createdAt).toLocaleDateString()}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="5" className="empty-state">No apps uploaded yet</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Invitations Section */}
            <div className="detail-section">
                <div className="section-header">
                    <Mail size={20} />
                    <h3>Pending Invitations ({workspace.invitations?.length || 0})</h3>
                </div>
                <div className="admin-table-container">
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th>Email</th>
                                <th>Role</th>
                                <th>Inviter</th>
                                <th>Sent</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {workspace.invitations?.length > 0 ? (
                                workspace.invitations.map(invite => (
                                    <tr key={invite.id}>
                                        <td>{invite.email}</td>
                                        <td>
                                            <span className={`role-badge ${invite.role}`}>
                                                {invite.role}
                                            </span>
                                        </td>
                                        <td>{invite.inviter?.email || 'System'}</td>
                                        <td>{new Date(invite.createdAt).toLocaleDateString()}</td>
                                        <td>
                                            <span className="status-badge warning">
                                                <Clock size={12} /> Pending
                                            </span>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="5" className="empty-state">No pending invitations</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

export default AdminWorkspaceDetail;
